import { memo } from "react";
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { WorkspacePremium, CheckCircle, WarningAmber, OpenInNew } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { usePlan, formatPlanDate } from "../../Hooks/usePlan";
import { useSiteConfig } from "../../Hooks/useSiteConfig";
import { usePostBillingPortalMutation } from "../../Services/api.user.slice";

/**
 * My Account → Plan (November 2026, Task 5). Shows the signed-in owner's plan:
 * Free (→ Get Premium), Premium paid (renewal/cancel date + Manage billing), or
 * a comp grant (gift/escrow until a date, with "Subscribe to keep it"). A failed
 * renewal (past_due) shows an "update your card" banner.
 */
const SOURCE_LABEL: Record<string, string> = {
  escrow: "a free year with your home",
  gift: "a gift from SimplerHome",
  launch: "our launch gift",
};

const AccountPlanCard = memo(() => {
  const navigate = useNavigate();
  const { config } = useSiteConfig();
  const { plan, isLoading } = usePlan();
  const [portal, { isLoading: openingPortal }] = usePostBillingPortalMutation();

  const openPortal = async () => {
    try {
      const res: any = await portal().unwrap();
      if (res?.url) window.location.href = res.url;
      else toast.error("Couldn't open billing. Please try again.");
    } catch {
      toast.error("Couldn't open billing. Please try again.");
    }
  };

  const isPremium = plan?.tier === "premium";
  const isPaid = isPremium && plan?.source === "paid";
  const isGrant = isPremium && !isPaid;
  const pastDue = plan?.status === "past_due";
  const until = formatPlanDate(plan?.until);
  const billingOpen = config.billingEnabled;

  return (
    <Card elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "16px" }} data-testid="account-plan-card">
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: "50%",
              backgroundColor: isPremium ? "primary.main" : "#9e9e9e",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <WorkspacePremium sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={600}>Plan</Typography>
            <Typography variant="body2" color="text.secondary">
              {isLoading && !plan ? "Loading…" : isPremium ? "SimplerHome Premium" : "Free plan"}
            </Typography>
          </Box>
          {isPremium && (
            <Chip
              icon={<CheckCircle />}
              color="success"
              label="Premium"
              size="small"
              sx={{ ml: "auto", fontWeight: 700 }}
            />
          )}
        </Box>

        {/* Failed renewal — keep access while Stripe retries, but nudge to fix. */}
        {pastDue && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ p: 2, mb: 2, borderRadius: "12px", backgroundColor: "#fff4e5", border: "1px solid #ffcc80" }}
            data-testid="plan-past-due"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <WarningAmber sx={{ color: "#ed6c02" }} />
              <Typography variant="body2">
                We couldn't take your last payment. Update your card to keep Premium.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              color="warning"
              onClick={openPortal}
              disabled={openingPortal}
              sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}
            >
              {openingPortal ? "Opening…" : "Update card"}
            </Button>
          </Stack>
        )}

        {/* Paid subscriber */}
        {isPaid && !pastDue && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {plan?.cancelAtPeriodEnd
              ? `Your Premium ends on ${until}. You can renew anytime from billing.`
              : until
              ? `Renews automatically on ${until}. $50/year.`
              : "Your Premium is active."}
          </Typography>
        )}

        {/* Comp grant */}
        {isGrant && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            You have Premium — {plan?.source ? SOURCE_LABEL[plan.source] || "a gift" : "a gift"}
            {until ? `, through ${until}. Subscribe to keep it after that.` : "."}
          </Typography>
        )}

        {/* Free */}
        {!isPremium && !isLoading && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            You're on the Free plan. Upgrade to unlock your full Home History, maintenance reminders,
            documents and unlimited photos.
          </Typography>
        )}

        {/* Actions */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          {isPaid && (
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={openPortal}
              disabled={openingPortal}
              sx={{ textTransform: "none", borderRadius: 2 }}
              data-testid="plan-manage-billing"
            >
              {openingPortal ? "Opening…" : "Manage billing"}
            </Button>
          )}
          {!isPaid && billingOpen && (
            <Button
              variant="contained"
              startIcon={<WorkspacePremium />}
              onClick={() => navigate("/premium")}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
              data-testid="plan-get-premium"
            >
              {isGrant ? "Subscribe to keep Premium" : "Get Premium"}
            </Button>
          )}
          {!isPaid && !billingOpen && !isPremium && (
            <Button
              variant="text"
              onClick={() => navigate("/premium")}
              sx={{ textTransform: "none" }}
            >
              See Premium (opens soon)
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
});

export default AccountPlanCard;
